// Cuantas fichas conservan su marca de "ya lo vi" en la copia del SERVIDOR.
//
// Lissette, 2026-08-23: "coloco idioma espanol y cuando lo refresco se vuelve a
// ingles". Las marcas de frescura son DOS entradas por ficha abierta y viven en
// el mismo bloque que el idioma, el tema y los favoritos. El bloque tenia un
// tope de 20.000 letras en el servidor y, pasado el tope, se descartaba entero
// y en silencio: medido con identificadores reales, eso ocurria en la ficha
// numero 32. Desde ahi el idioma no volvia a guardarse nunca. Ver E-118.
//
// El servidor ahora poda en vez de tirar, pero el navegador no tiene por que
// mandar cien fichas cada vez: se queda con las mas recientes, que es justo
// para lo que sirve una marca de "ya lo vi". Esto acota lo que viaja y lo que se
// guarda fuera; lo que queda en el navegador lo acota podarFrescuraDelNavegador.
const MAX_FICHAS_SINCRONIZADAS = 50;

const FRESCURA_RE = /^deskte:(lastVisited|lastSeenTab):/;

/** La ficha a la que pertenece una marca de frescura, o null si no lo es. */
function fichaDe(key) {
  if (!FRESCURA_RE.test(key)) return null;
  const i = key.lastIndexOf(':');
  return i > 0 ? key.slice(i + 1) : null;
}

/** La fecha mas tardia dentro del valor, para ordenar por "cual se toco antes". */
function selloDe(value) {
  const s = typeof value === 'string' ? value : '';
  if (!s) return '';
  const fechas = s.match(/\d{4}-\d{2}-\d{2}T[\d:.]+Z/g);
  return fechas && fechas.length ? fechas.sort()[fechas.length - 1] : s;
}

/**
 * Devolver un `kv` con las marcas de frescura acotadas a las ultimas
 * MAX_FICHAS_SINCRONIZADAS fichas. Todo lo que NO es una marca de frescura
 * (tamanos de tabla, ultima ficha abierta, borradores) se queda intacto.
 *
 * Las dos claves de una misma ficha se van juntas: media marca no sirve.
 */
export function podarFrescura(kv) {
  if (!kv || typeof kv !== 'object') return kv;
  const porFicha = new Map();
  for (const k of Object.keys(kv)) {
    const f = fichaDe(k);
    if (!f) continue;
    const sello = selloDe(kv[k]);
    const prev = porFicha.get(f);
    if (prev) {
      prev.keys.push(k);
      if (sello > prev.sello) prev.sello = sello;
    } else {
      porFicha.set(f, { keys: [k], sello });
    }
  }
  if (porFicha.size <= MAX_FICHAS_SINCRONIZADAS) return kv;

  const sobran = [...porFicha.values()]
    .sort((a, b) => (a.sello < b.sello ? 1 : a.sello > b.sello ? -1 : 0))  // recientes primero
    .slice(MAX_FICHAS_SINCRONIZADAS);

  const out = { ...kv };
  for (const ficha of sobran) for (const k of ficha.keys) delete out[k];
  return out;
}

// Cuantas fichas conservan su marca de "ya lo vi" en ESTE navegador.
//
// Lissette, 2026-10-27 (aviso E6): el comentario de arriba decia "el
// localStorage de este equipo las conserva todas", y eso era el problema: dos
// claves por ficha abierta y por usuario (cada "Ver como" suma las suyas), sin
// borrarse nunca. En su navegador eran 5.400 claves y ayudaban a llenarlo hasta
// que dejaba de guardar la entrada de Platform Admin. Una ficha que se cae de la
// lista solo vuelve a contar como "primera visita": no sale la bolita de nuevo.
export const MAX_FICHAS_EN_NAVEGADOR = 300;

/**
 * Quitar del almacen las marcas de frescura de las fichas mas viejas, dejando
 * las MAX_FICHAS_EN_NAVEGADOR mas recientes. Agrupa por usuario + ficha (lo que
 * va despues del prefijo), y las dos claves de una ficha se van juntas. Borra
 * DIRECTO en el almacen, sin pasar por prefsRemove: la copia del servidor ya se
 * poda sola (podarFrescura) y no hay que mandarle nada. Devuelve cuantas quito.
 */
export function podarFrescuraDelNavegador(almacen, max = MAX_FICHAS_EN_NAVEGADOR) {
  if (!almacen) return 0;
  const porFicha = new Map();
  for (let i = 0; i < almacen.length; i++) {
    const k = almacen.key(i);
    if (!k || !FRESCURA_RE.test(k)) continue;
    const ficha = k.replace(FRESCURA_RE, '');
    const sello = selloDe(almacen.getItem(k));
    const prev = porFicha.get(ficha);
    if (prev) {
      prev.keys.push(k);
      if (sello > prev.sello) prev.sello = sello;
    } else {
      porFicha.set(ficha, { keys: [k], sello });
    }
  }
  if (porFicha.size <= max) return 0;
  const sobran = [...porFicha.values()]
    .sort((a, b) => (a.sello < b.sello ? 1 : a.sello > b.sello ? -1 : 0))  // recientes primero
    .slice(max);
  let quitadas = 0;
  for (const ficha of sobran) {
    for (const k of ficha.keys) { almacen.removeItem(k); quitadas++; }
  }
  return quitadas;
}

// Tiny string key→value store for per-user UI conveniences (table page sizes,
// last opened client/tab, "unread" freshness stamps). Writes go to localStorage
// (synchronous → instant mount-time reads) AND are mirrored into the logged-in
// user's uiPrefs, which deskte-full syncs to the server. On login, deskte-full
// seeds localStorage from the server copy so these follow the user across
// devices. Before a mirror is installed (or when logged out) it degrades to
// plain localStorage, so nothing breaks pre-login.

let _mirror = null; // (key, value|null) => void

// Install the function that mirrors a write into uiPrefs. deskte-full calls this
// once, after the user (and their prefs) are available.
export function installPrefsMirror(fn) { _mirror = fn; }

// Copy a server kv map into localStorage so synchronous reads at component mount
// pick up the user's saved values on this device. Call on login / prefs hydration.
export function seedPrefsFromServer(kv) {
  if (!kv || typeof kv !== 'object') return;
  for (const k of Object.keys(kv)) {
    try { if (kv[k] != null) localStorage.setItem(k, String(kv[k])); } catch { /* quota / private mode */ }
  }
}

export function prefsGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

export function prefsSet(key, value) {
  try { localStorage.setItem(key, String(value)); } catch { /* quota / private mode */ }
  if (_mirror) { try { _mirror(key, String(value)); } catch { /* non-fatal */ } }
}

export function prefsRemove(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  if (_mirror) { try { _mirror(key, null); } catch { /* non-fatal */ } }
}
